import {IDirective, IOnDestroy, IOnInit, IRootElementService} from "angular";
import {QuoteViewModel} from "../models/QuoteViewModel";
import {BehaviorSubject, ReplaySubject, Scheduler} from "rxjs";
import {isNotNull} from "../../js/util/UtilFunctions";
import {QuoteChangeDirection} from "../models/QuoteChangeDirection";

export const QuoteChange = "tfQuoteChange";

export function quoteChange(): IDirective {
    return {
        restrict: "A",
        controllerAs: "ctrl",
        bindToController: {
            quote: "<tfQuoteChange"
        },
        controller: QuoteChangeDirectiveController,
        scope: {}
    };
}

const QuoteChangeUp = "quote-change-up";
const QuoteChangeDown = "quote-change-down";

export class QuoteChangeDirectiveController implements IOnInit, IOnDestroy {

    /** @input */
    public set quote(quote: QuoteViewModel | null) {
        this.quote$.next(quote);
    }

    private readonly quote$ = new BehaviorSubject<QuoteViewModel | null>(null);
    private readonly className$ = new ReplaySubject<string | null>(1);
    private readonly animationEndedFn = this.animationEnded.bind(this);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $element: IRootElementService) {
        this.$element.addClass("tf-quote-change-target");
    }

    public $onInit(): void {
        this.watchQuoteChange();
        this.addAnimationListener();
        this.watchClassName();
    }

    private watchQuoteChange() {
        this.quote$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((quote) => quote.changeDirection)
            .map((direction) => this.directionClass(direction))
            .subscribe((className) => this.className$.next(className));
    }

    private directionClass(direction: QuoteChangeDirection): string | null {
        switch (direction) {
            case QuoteChangeDirection.NO_CHANGE:
                return null;
            case QuoteChangeDirection.UP:
                return QuoteChangeUp;
            case QuoteChangeDirection.DOWN:
                return QuoteChangeDown;
        }
    }

    private addAnimationListener() {
        this.element.addEventListener("animationend", this.animationEndedFn, {passive: true});
    }

    private animationEnded(event: AnimationEvent) {
        this.className$.next(null);
    }

    private removeAnimationListener() {
        this.element.removeEventListener("animationend", this.animationEndedFn);
    }

    private watchClassName() {
        this.className$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .subscribe((className) => {
                if (className == null) {
                    this.element.classList.remove(QuoteChangeUp, QuoteChangeDown);
                } else if (className === QuoteChangeUp) {
                    this.element.classList.remove(QuoteChangeDown);
                    this.element.classList.add(QuoteChangeUp);
                } else if (className === QuoteChangeDown) {
                    this.element.classList.remove(QuoteChangeUp);
                    this.element.classList.add(QuoteChangeDown);
                }
            });
    }

    private get element(): HTMLElement {
        return this.$element[0];
    }

    public $onDestroy(): void {
        this.removeAnimationListener();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
